import type { Proxy } from '#~/agent/index.js';
import * as Dial from '@studio/design-system/dial';
import type { DeepPartial } from '@tryiris-ai/common.protojs';
import type { Control } from '@tryiris-ai/common.protojs/agent/v1/agent';
import {
  DeviceRegistryCommandFunctionType,
  DeviceRegistryCommandKey,
  DeviceRegistryInterfaceItemControlType
} from '@tryiris-ai/common.protojs/api/v1/device_settings';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { noop } from 'lodash-es';
import type { ReactElement } from 'react';
import type { SettingsStatus } from './status-notifications.js';
import { STATUS_NOTIFICATIONS } from './status-notifications.js';
import type { Device } from '@studio/models/device';

interface DeviceSettingDialProps {
  agent?: T_Maybe<Proxy>;
  className: string;
  cmdKey: DeviceRegistryCommandKey;
  defaultValue: T_Maybe<number>;
  deviceId: Device['id'];
  disabled?: boolean;
  items: { label: string; value: number }[];
  label: string;
  onMutation: T_Maybe<(mutation: DeepPartial<Control>) => void>;
  value: number;
  onControlClick?: (controlData: {
    label: string;
    controlType: DeviceRegistryInterfaceItemControlType;
    key: DeviceRegistryCommandKey;
    value?: DeviceRegistryCommandFunctionType;
  }) => void;
  status?: SettingsStatus;
}

/** `<DeviceSettingDial>` component. */
export const DeviceSettingDial = ({
  agent,
  className,
  cmdKey,
  defaultValue,
  deviceId,
  disabled,
  items,
  label,
  onMutation,
  value,
  onControlClick,
  status
}: DeviceSettingDialProps): ReactElement => {
  const mutateDialValue = (nextValue: number): void => {
    const control: DeepPartial<Control> = {
      device: {
        [deviceId]: {
          deviceSettings: {
            [String(cmdKey)]: {
              direct: nextValue,
              key: cmdKey,
              lastModified: Number(new Date())
            }
          }
        }
      }
    };

    if (onMutation) {
      onMutation(control);
      onControlClick?.({
        label,
        controlType: DeviceRegistryInterfaceItemControlType.DIAL,
        key: cmdKey,
        value: DeviceRegistryCommandFunctionType.VALUE_SET
      });

      return;
    }

    agent
      ?.mutate(
        {
          control
        },
        {}
      )
      .catch(noop);

    onControlClick?.({
      label,
      controlType: DeviceRegistryInterfaceItemControlType.DIAL,
      key: cmdKey,
      value: DeviceRegistryCommandFunctionType.VALUE_SET
    });
  };

  const getIndex = (v: number): number => {
    const distances = items.map((item) => Math.abs(item.value - v));

    return distances.indexOf(Math.min(...distances));
  };

  const props = {
    items,
    onChange: (dialItem: Dial.Item, _: number): void => {
      const nextValue = Number(
        typeof dialItem === 'object' ? (dialItem as { value: number }).value : dialItem
      );

      mutateDialValue(nextValue);
    }
  };

  const index = getIndex(value);

  return (
    <Dial.Container
      canRevert={!disabled && defaultValue !== undefined && value !== defaultValue}
      className={className}
      label={label}
      status={status}
      notification={status ? STATUS_NOTIFICATIONS[status] : undefined}
      onRevert={(): void => {
        mutateDialValue(defaultValue!);
      }}
    >
      <Dial.Root selectedItem={items[index]} {...props} disabled={disabled} />
    </Dial.Container>
  );
};
